// Тема оформления: светлая, тёмная и «как в системе».
//
// ## Почему она появилась только 02.09.2026
//
// Тёмная тема была НАРИСОВАНА с самого начала: `darkMode: 'class'` объявлен в настройке
// оформления, а в `app/globals.css` расписаны все 68 значений цвета для `.dark`. Не хватало
// одной вещи — того, кто вешает этот класс. Его не вешал никто, поэтому ни один экран
// программы тёмную тему ни разу не показывал, и вся работа лежала мёртвой.
//
// ## Почему выбор из трёх, а не переключатель из двух
//
// «Как в системе» — не третья тема, а отказ выбирать: человек один раз настроил Windows и
// ждёт, что программы это уважают. Переключатель из двух положений заставляет выбирать
// заново в каждой программе и разъезжается с системой при первом же переходе на тёмную
// осенью. Поэтому по умолчанию мы идём за системой, а явный выбор — это «здесь хочу иначе».
//
// ## Где хранится
//
// В том же месте, что и адрес сервера (`shared/api/server-address.ts`), и по той же причине:
// десктоп у нас обёртка над вебом, и один механизм работает в обеих формах. Настройка живёт
// на устройстве, а не в учётной записи: тёмная тема — про монитор и освещение в комнате, а
// не про человека. Один и тот же финдиректор дома и в офисе хочет разного.

const КЛЮЧ = 'bark.theme';

/** Что выбрал человек. «Система» — по умолчанию: программа идёт за настройкой Windows. */
export type Тема = 'система' | 'светлая' | 'тёмная';

const ЗНАЧЕНИЯ: readonly Тема[] = ['система', 'светлая', 'тёмная'];

/** Выбор человека. Ничего не выбрано — «система». */
export function выбраннаяТема(): Тема {
  try {
    const значение = localStorage.getItem(КЛЮЧ);
    return ЗНАЧЕНИЯ.includes(значение as Тема) ? (значение as Тема) : 'система';
  } catch {
    // Хранилище недоступно (частный режим, запрет в обёртке) — идём за системой.
    return 'система';
  }
}

/** Запомнить выбор и применить его немедленно. */
export function выбратьТему(тема: Тема): void {
  try {
    localStorage.setItem(КЛЮЧ, тема);
  } catch {
    // Не запомнили — но применить обязаны: иначе нажатие выглядит несработавшим.
  }
  применитьТему(тема);
}

/** Тёмная ли сейчас в системе. */
export function системнаяТёмная(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : false;
}

/** Повесить или снять класс `dark` на корне страницы. Единственное место, где это делается. */
export function применитьТему(тема: Тема): void {
  const тёмная = тема === 'тёмная' || (тема === 'система' && системнаяТёмная());
  const корень = document.documentElement;
  корень.classList.toggle('dark', тёмная);
  // Системные органы управления — полосы прокрутки, календарь в поле даты, выделение текста —
  // рисуются браузером, а не нами. Без этой подсказки на тёмном экране остаётся белая полоса
  // прокрутки, и она видна сразу.
  корень.style.colorScheme = тёмная ? 'dark' : 'light';
}

/**
 * Следить за настройкой системы, пока выбрана «система».
 *
 * Возвращает отписку. Слежка нужна вживую: человек переключает тему Windows по расписанию
 * (светлая днём, тёмная вечером), и программа, узнающая об этом только при перезапуске, до
 * вечера остаётся белым пятном на тёмном столе.
 */
export function следитьЗаСистемой(получить: () => Тема): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return () => undefined;
  }
  const наблюдение = window.matchMedia('(prefers-color-scheme: dark)');
  const обработчик = () => {
    if (получить() === 'система') {
      применитьТему('система');
    }
  };
  наблюдение.addEventListener('change', обработчик);
  return () => {
    наблюдение.removeEventListener('change', обработчик);
  };
}
